// The agreement's signing page on its own: /apply/<token>/sign. The Lotus Living app opens it in a WebView for a
// booking made in the app, so the tenant reads the agreement and signs inside its signature box exactly as on the web
// page (/apply/<token>, the Sign step) — the same AgreementViewer and SignatureBox, the same POST, and the server
// stamps the drawing into the PDF in the same place. No site header or footer: it sits inside the app's own screen.
//
// It tells the app what happened through window.ReactNativeWebView.postMessage (JSON): { type: 'signed' } once the
// agreement is signed (or already was), { type: 'error', message } when it cannot be opened. Opened in a browser it
// simply works as a page.
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Lock } from 'lucide-react';
import { AgreementViewer, SignatureBox } from '../../components/site/AgreementViewer';
import { PillButton } from '../../components/site/Pill';
import { applicantApi, type ApplicantView } from '../../lib/reservations';
import { formatDate } from '../../lib/viz';

type AppMessage = { type: 'signed' } | { type: 'error'; message: string };
const tellApp = (m: AppMessage) => (window as unknown as { ReactNativeWebView?: { postMessage: (s: string) => void } }).ReactNativeWebView?.postMessage(JSON.stringify(m));

export default function ApplicantSignPage() {
  const { token = '' } = useParams<{ token: string }>();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['applicant', token], queryFn: () => applicantApi.view(token), enabled: !!token, retry: false });
  const v = q.data;
  const [name, setName] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [drawn, setDrawn] = useState<string | null>(null);
  const [typing, setTyping] = useState(false);
  const fullNameValue = name ?? v?.applicant.name ?? '';
  const m = useMutation({
    mutationFn: () => applicantApi.sign(token, { fullName: fullNameValue, agreed, signatureDataUrl: typing ? null : drawn }),
    onSuccess: (d: ApplicantView) => { qc.setQueryData(['applicant', token], d); tellApp({ type: 'signed' }); },
  });
  useEffect(() => { if (v?.agreement.signedAt) tellApp({ type: 'signed' }); }, [v?.agreement.signedAt]);
  useEffect(() => { if (q.error) tellApp({ type: 'error', message: (q.error as Error).message }); }, [q.error]);

  if (q.isLoading) return <Frame><p className="py-24 text-center text-[14px] text-ink-400">Opening your agreement…</p></Frame>;
  if (!v) return <Frame><div className="py-20 text-center"><Lock className="mx-auto h-9 w-9 text-ink-300" /><p className="mt-4 text-[15px] text-ink-600">This agreement cannot be opened here.</p></div></Frame>;

  const a = v.agreement;
  if (a.signedAt) {
    return (
      <Frame>
        <p className="flex items-start gap-2 text-[14px] text-emerald-700"><Check className="mt-0.5 h-4 w-4 shrink-0" />Signed by {a.signedBy} on {formatDate(a.signedAt)}.</p>
        {a.signed && <div className="mt-4"><AgreementViewer key={a.signed.url} url={a.signed.url} /></div>}
      </Frame>
    );
  }
  if (!a.toSign) return <Frame><p className="py-20 text-center text-[14px] text-ink-500">{v.steps.find((s) => s.key === 'sign')?.lockedBy ?? 'Your agreement is not ready yet.'}</p></Frame>;

  const twoWords = fullNameValue.trim().split(/\s+/).length >= 2;
  const ok = agreed && twoWords && (typing || !!drawn);
  return (
    <Frame>
      <form onSubmit={(e) => { e.preventDefault(); if (ok) m.mutate(); }} className="space-y-4">
        <p className="text-[14px] leading-relaxed text-ink-600">Read it, then sign in the <b className="font-medium text-ink-900">Sign here</b> box on the last page.</p>
        <AgreementViewer key={a.toSign.url} url={a.toSign.url} overlay={<SignatureBox typed={typing ? fullNameValue.trim() || null : null} onChange={setDrawn} />} />
        <button type="button" onClick={() => setTyping((t) => !t)} className="inline-flex min-h-[44px] items-center text-[14px] font-medium text-bronze-700 underline underline-offset-4 hover:text-ink-900">{typing ? 'Draw my signature instead' : 'Type my name instead'}</button>
        {(typing || !twoWords) && (
          <label className="site-field"><span className="site-label">Full name, as on your ID *</span>
            <input autoComplete="name" value={fullNameValue} onChange={(e) => setName(e.target.value)} className="site-input" /></label>
        )}
        <label className="site-check !text-[14px]">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span>I have read the tenancy agreement and the particulars at the front of it, and I agree to be bound by them. I understand this is my electronic signature. *</span>
        </label>
        {m.error && <p className="site-error">{(m.error as Error).message}</p>}
        <PillButton type="submit" disabled={m.isPending || !ok} className="w-full disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto">{m.isPending ? 'Signing…' : 'Sign the agreement'}</PillButton>
      </form>
    </Frame>
  );
}

const Frame = ({ children }: { children: React.ReactNode }) => <div className="min-h-screen bg-white px-4 pb-[calc(2.5rem+env(safe-area-inset-bottom))] pt-4 sm:px-6"><div className="mx-auto max-w-3xl">{children}</div></div>;
